import { BufferGeometry, Float32BufferAttribute, ShapeUtils, Vector2 } from 'three'

/**
 * Creates a BufferGeometry for a box with origin at one corner instead of the center.
 * Vertex positions are written explicitly into the buffer — DO NOT simplify this by
 * using `new BoxGeometry()` + `geometry.translate()`. While translate() visually works,
 * Three.js JSON export serializes raw vertex coordinates and does NOT persist the
 * translation offset; the geometry silently reverts to a center-origin box after any
 * scene JSON round-trip (export → re-import / page refresh).
 *
 * The box spans from (0, 0, 0) to (width, depth, height). Negative values are
 * supported: the box then extends in the negative direction from the origin,
 * which allows drawing boxes by dragging in any direction.
 *
 * @param width  - Size along X axis (can be negative)
 * @param depth  - Size along Y axis (can be negative)
 * @param height - Size along Z axis (can be negative)
 */
export function makeBoxGeometryFromCorner(width: number, depth: number, height: number): BufferGeometry {
  const xmin = Math.min(0, width)
  const xmax = Math.max(0, width)
  const ymin = Math.min(0, depth)
  const ymax = Math.max(0, depth)
  const zmin = Math.min(0, height)
  const zmax = Math.max(0, height)

  // 6 faces × 4 vertices each = 24 vertices
  // Winding is CCW when viewed from outside
  const positions = [
    // +Z (front)
    xmin, ymin, zmax,
    xmax, ymin, zmax,
    xmax, ymax, zmax,
    xmin, ymax, zmax,
    // -Z (back)
    xmax, ymin, zmin,
    xmin, ymin, zmin,
    xmin, ymax, zmin,
    xmax, ymax, zmin,
    // +X (right)
    xmax, ymin, zmax,
    xmax, ymin, zmin,
    xmax, ymax, zmin,
    xmax, ymax, zmax,
    // -X (left)
    xmin, ymin, zmin,
    xmin, ymin, zmax,
    xmin, ymax, zmax,
    xmin, ymax, zmin,
    // +Y (top)
    xmin, ymax, zmax,
    xmax, ymax, zmax,
    xmax, ymax, zmin,
    xmin, ymax, zmin,
    // -Y (bottom)
    xmin, ymin, zmin,
    xmax, ymin, zmin,
    xmax, ymin, zmax,
    xmin, ymin, zmax,
  ]

  const normals = [
    // +Z
    0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1,
    // -Z
    0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1,
    // +X
    1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0,
    // -X
    -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0, 0,
    // +Y
    0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0,
    // -Y
    0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0,
  ]

  const faceUV = [0, 0, 1, 0, 1, 1, 0, 1]
  const uvs = [...faceUV, ...faceUV, ...faceUV, ...faceUV, ...faceUV, ...faceUV]

  // 6 faces × 2 triangles × 3 indices
  const indices: number[] = []
  for (let f = 0; f < 6; f++) {
    const o = f * 4
    indices.push(o, o + 1, o + 2, o, o + 2, o + 3)
  }

  const geometry = new BufferGeometry()
  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3))
  geometry.setAttribute('normal', new Float32BufferAttribute(normals, 3))
  geometry.setAttribute('uv', new Float32BufferAttribute(uvs, 2))
  geometry.setIndex(indices)
  geometry.computeBoundingBox()
  geometry.computeBoundingSphere()

  return geometry
}

/**
 * Creates a BufferGeometry for an arbitrary polygon footprint extruded along Z.
 * Like makeBoxGeometryFromCorner, vertices are written explicitly (no ExtrudeGeometry)
 * so the shape survives the scene JSON export/import round-trip without relying on
 * parametric "shapes" metadata.
 *
 * The footprint winding is normalized to counter-clockwise internally so outward-facing
 * normals are correct regardless of the order the points were drawn in.
 *
 * @param points - Polygon footprint vertices in the local XY (ground) plane
 * @param height - Extrusion height along Z (must be positive)
 */
export function makeExtrudedPolygonGeometry(points: { x: number, y: number }[], height: number): BufferGeometry {
  const ccwPoints = ShapeUtils.isClockWise(points) ? [...points].reverse() : points
  const vertices2d = ccwPoints.map(p => new Vector2(p.x, p.y))
  const triangles = ShapeUtils.triangulateShape(vertices2d, [])
  const count = ccwPoints.length

  const positions: number[] = []
  const normals: number[] = []
  const uvs: number[] = []
  const indices: number[] = []
  let vertexCount = 0

  const pushTriangle = (
    p0: { x: number, y: number, z: number },
    p1: { x: number, y: number, z: number },
    p2: { x: number, y: number, z: number },
    normal: { x: number, y: number, z: number },
  ) => {
    positions.push(p0.x, p0.y, p0.z, p1.x, p1.y, p1.z, p2.x, p2.y, p2.z)
    for (let i = 0; i < 3; i++) normals.push(normal.x, normal.y, normal.z)
    uvs.push(0, 0, 1, 0, 0, 1)
    indices.push(vertexCount, vertexCount + 1, vertexCount + 2)
    vertexCount += 3
  }

  // bottom cap (z = 0), facing down
  for (const [a, b, c] of triangles) {
    pushTriangle(
      { x: ccwPoints[a].x, y: ccwPoints[a].y, z: 0 },
      { x: ccwPoints[c].x, y: ccwPoints[c].y, z: 0 },
      { x: ccwPoints[b].x, y: ccwPoints[b].y, z: 0 },
      { x: 0, y: 0, z: -1 },
    )
  }

  // top cap (z = height), facing up
  for (const [a, b, c] of triangles) {
    pushTriangle(
      { x: ccwPoints[a].x, y: ccwPoints[a].y, z: height },
      { x: ccwPoints[b].x, y: ccwPoints[b].y, z: height },
      { x: ccwPoints[c].x, y: ccwPoints[c].y, z: height },
      { x: 0, y: 0, z: 1 },
    )
  }

  // side walls, one outward-facing quad (2 triangles) per edge
  for (let i = 0; i < count; i++) {
    const j = (i + 1) % count
    const p0 = ccwPoints[i]
    const p1 = ccwPoints[j]
    const edgeX = p1.x - p0.x
    const edgeY = p1.y - p0.y
    const edgeLength = Math.hypot(edgeX, edgeY) || 1
    const normal = { x: edgeY / edgeLength, y: -edgeX / edgeLength, z: 0 }

    const bottom0 = { x: p0.x, y: p0.y, z: 0 }
    const bottom1 = { x: p1.x, y: p1.y, z: 0 }
    const top0 = { x: p0.x, y: p0.y, z: height }
    const top1 = { x: p1.x, y: p1.y, z: height }

    pushTriangle(bottom0, bottom1, top1, normal)
    pushTriangle(bottom0, top1, top0, normal)
  }

  const geometry = new BufferGeometry()
  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3))
  geometry.setAttribute('normal', new Float32BufferAttribute(normals, 3))
  geometry.setAttribute('uv', new Float32BufferAttribute(uvs, 2))
  geometry.setIndex(indices)
  geometry.computeBoundingBox()
  geometry.computeBoundingSphere()

  return geometry
}
